import {
    parseRequestExamplesAuthTokenResultMessageData,
    type RequestExamplesAuthTokenRequestMessage,
} from "@albertine/types";
import { parentOriginFromLocation, postMessageToParentFrame } from "./parentFrame";

const TOKEN_REQUEST_TIMEOUT_MS = 5000;

export type ParentFrameTokenOptions = {
    forceRefresh?: boolean;
};

export async function getKeycloakTokenFromParentFrame(
    options?: ParentFrameTokenOptions,
): Promise<string | null> {
    const parentOrigin = parentOriginFromLocation();
    if (!parentOrigin || window.parent === window) {
        return null;
    }

    return requestTokenFromParentFrame(parentOrigin, options);
}

export function notifyParentUnauthorized() {
    postMessageToParentFrame(parentOriginFromLocation(), {
        type: "requestExamples.auth.unauthorized",
    });
}

function requestTokenFromParentFrame(
    parentOrigin: string,
    options?: ParentFrameTokenOptions,
): Promise<string | null> {
    const requestId = tokenRequestId();
    const requestMessage: RequestExamplesAuthTokenRequestMessage = {
        type: "requestExamples.auth.token.request",
        requestId,
        forceRefresh: options?.forceRefresh,
    };

    return new Promise((resolve) => {
        const timeoutId = window.setTimeout(() => {
            window.removeEventListener("message", receiveTokenMessage);
            resolve(null);
        }, TOKEN_REQUEST_TIMEOUT_MS);

        function finish(token: string | null) {
            window.clearTimeout(timeoutId);
            window.removeEventListener("message", receiveTokenMessage);
            resolve(token);
        }

        function receiveTokenMessage(event: MessageEvent<unknown>) {
            if (event.origin !== parentOrigin || event.source !== window.parent) {
                return;
            }

            const message = parseRequestExamplesAuthTokenResultMessageData(event.data);

            if (!message || message.requestId !== requestId) {
                return;
            }

            if (message.type === "requestExamples.auth.token.response") {
                finish(message.accessToken);
                return;
            }

            finish(null);
        }

        window.addEventListener("message", receiveTokenMessage);
        postMessageToParentFrame(parentOrigin, requestMessage);
    });
}

function tokenRequestId(): string {
    return crypto.randomUUID();
}
